/** @format */

import { type ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileDropZone from '../FileDropZone';
import { DeleteIcon, EditIcon, ImageEditorIcon, ThreedotsIcon } from 'src/components/icons';
import type { ContentEditorPart, ContentPartUploadFn } from 'src/types';

interface ContentEditorBlockBodyImageProps {
  part: ContentEditorPart;
  onFile?: ContentPartUploadFn;
}

export default function ContentEditorBlockBodyImage({ part, onFile = undefined }: ContentEditorBlockBodyImageProps) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const imagePart = part.type === 'image' ? part : null;
  const url = imagePart?.image && typeof imagePart.image.url === 'string' ? imagePart.image.url : '';
  const showPreview = Boolean(url);
  const uploadFile = Array.isArray(imagePart?._file) ? imagePart._file[0] : imagePart?._file;
  const displayName = uploadFile?.name || (imagePart?.image?.title as string | undefined) || '';
  const inputId = `content-editor-file-${part.id}`;

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const handleChangeImage = () => {
    setMenuOpen(false);
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleDeleteImage = () => {
    setMenuOpen(false);
    if (typeof onFile === 'function') {
      onFile(null);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    if (file && typeof onFile === 'function') {
      onFile(file);
    }
    if (event?.target) {
      event.target.value = '';
    }
  };

  return (
    <div className="content-editor__image-block">
      {showPreview ? (
        <div className="content-editor__image-preview-wrap">
          <div className="content-editor__remote-image-preview">
            <img
              src={url}
              alt={typeof imagePart?.image?.alt === 'string' ? imagePart.image.alt : ''}
              className="content-editor__remote-image-preview-img"
            />
          </div>
          <div className="content-editor__image-actions" ref={menuRef}>
            <button
              type="button"
              className="content-editor__image-actions-toggle"
              onClick={() => setMenuOpen(open => !open)}
              aria-label={t('contentEditor.imageActions')}
              aria-expanded={menuOpen}>
              <ThreedotsIcon />
            </button>
            {menuOpen ? (
              <div className="content-editor__image-actions-menu" role="menu">
                <button
                  type="button"
                  className="content-editor__image-actions-item"
                  role="menuitem"
                  onClick={handleChangeImage}>
                  <EditIcon width={20} height={20} />
                  <span>{t('common.button.change')}</span>
                </button>
                <button
                  type="button"
                  className="content-editor__image-actions-item"
                  role="menuitem"
                  onClick={handleDeleteImage}>
                  <DeleteIcon width={20} height={20} />
                  <span>{t('common.button.delete')}</span>
                </button>
              </div>
            ) : null}
            <input
              id={inputId}
              ref={inputRef}
              type="file"
              accept="image/*"
              className="content-editor__dropzone-input"
              onChange={handleFileChange}
            />
          </div>
        </div>
      ) : null}
      {!showPreview ? (
        <FileDropZone
          inputId={inputId}
          accept="image/*"
          className="content-editor__dropzone--image-block"
          leading={<ImageEditorIcon />}
          label={t('contentEditor.imageBlockDropLabel')}
          fileName={displayName}
          onFile={onFile}
        />
      ) : null}
    </div>
  );
}
